import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, SERVICES_IMAGE } from "../lib/constants";
import { MaskedLines, Reveal } from "./motion";

export default function Services() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="servicios"
      ref={ref}
      data-testid="services-section"
      className="relative overflow-hidden py-24 text-white lg:py-32"
      style={{ backgroundColor: "hsl(var(--pv-deep-green))" }}
    >
      <div className="absolute inset-0 bg-blueprint-dark opacity-50" />
      <span
        aria-hidden
        className="text-outline pointer-events-none absolute -left-6 top-6 hidden select-none font-display text-[17rem] font-extrabold leading-none opacity-60 lg:block"
      >
        02
      </span>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-16 grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="overline mb-4 text-pv-lime">
                Capítulo 02 — Proceso
              </div>
            </Reveal>
            <MaskedLines
              className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
              lines={[
                <>Un proceso completo,</>,
                <span className="text-pv-orange">de la auditoría al equipo.</span>,
              ]}
            />
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <p className="text-base leading-relaxed text-white/75 lg:text-lg">
                No vendemos equipo aislado. Acompañamos a su empresa con un
                proceso integral que reduce riesgos reales y costos invisibles.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid gap-px overflow-hidden rounded-sm border border-white/10 bg-white/10 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal
              key={s.id}
              delay={i * 0.12}
              data-testid={`service-card-${s.id}`}
              className="group relative bg-[hsl(var(--pv-deep-green))] p-8 transition-colors duration-500 hover:bg-[hsl(var(--pv-dark-green))] lg:p-10"
            >
              <span
                aria-hidden
                className="text-outline pointer-events-none absolute right-6 top-4 select-none font-display text-7xl font-extrabold leading-none opacity-50 transition-opacity duration-500 group-hover:opacity-100"
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="mb-8 flex items-center justify-between">
                <span className="overline text-pv-lime">{s.overline}</span>
                <ArrowUpRight
                  size={20}
                  className="text-white/40 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-pv-orange"
                />
              </div>
              <h3 className="mb-4 font-display text-2xl font-bold tracking-tight lg:text-3xl">
                {s.title}
              </h3>
              <p className="mb-6 text-sm leading-relaxed text-white/70 lg:text-base">
                {s.desc}
              </p>
              <ul className="space-y-2 border-t border-white/15 pt-6">
                {s.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-center gap-2 text-sm text-white/85"
                  >
                    <span className="h-1.5 w-1.5 bg-pv-orange" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
          <Reveal className="overflow-hidden rounded-sm border border-white/10">
            <motion.img
              src={SERVICES_IMAGE}
              alt="Sitio de construcción"
              style={{ y: imgY, scale: 1.15 }}
              className="aspect-[16/10] w-full object-cover will-change-transform"
            />
          </Reveal>
          <div>
            <MaskedLines
              className="mb-4 font-display text-2xl font-bold tracking-tight lg:text-3xl"
              lines={[
                <>Tranquilidad para la empresa.</>,
                <span className="text-pv-lime">Protección para el colaborador.</span>,
              ]}
            />
            <Reveal delay={0.15}>
              <p className="leading-relaxed text-white/75">
                Nuestro objetivo es que cada trabajador regrese sano a casa y
                que cada empresa opere sin interrupciones por accidentes
                evitables. Trabajamos en industria, construcción, alimentos,
                salud, química, limpieza y más.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
